import "@/App.css";
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { MnemonicProvider } from "@/contexts/MnemonicContext";
import MnemonicDevOverlay from "@/components/mnemonics/MnemonicDevOverlay";
import { AGGREGATE_MONEY_ROLES, ORDER_EDITOR_ROLES, USER_MANAGER_ROLES, mustChangePassword } from "@/lib/constants";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import CreateOrderPage from "@/pages/CreateOrderPage";
import EditOrderPage from "@/pages/EditOrderPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerProfilePage from "@/pages/CustomerProfilePage";
import UsersPage from "@/pages/UsersPage";
import DesignQueuePage from "@/pages/DesignQueuePage";
import MyDesignsPage from "@/pages/MyDesignsPage";
import ProductionPage from "@/pages/ProductionPage";
import MyProductionQueuePage from "@/pages/MyProductionQueuePage";
import DispatchPage from "@/pages/DispatchPage";
import InventoryPage from "@/pages/InventoryPage";
import PaymentsPage from "@/pages/PaymentsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import NetworkAccessPage from "@/pages/NetworkAccessPage";
import JobCardPage from "@/pages/JobCardPage";
import JobCardFillPage from "@/pages/JobCardFillPage";
import JobCardsPage from "@/pages/JobCardsPage";
import MachinesPage from "@/pages/MachinesPage";
import ExternalProcessingPage from "@/pages/ExternalProcessingPage";
import VendorManagementPage from "@/pages/VendorManagementPage";
import ProductCatalogPage from "@/pages/ProductCatalogPage";
import CreateCatalogItemPage from "@/pages/CreateCatalogItemPage";
import CatalogItemDetailPage from "@/pages/CatalogItemDetailPage";
import EditCatalogItemPage from "@/pages/EditCatalogItemPage";
import PlateTrackingPage from "@/pages/PlateTrackingPage";
import SupervisorDashboardPage from "@/pages/SupervisorDashboardPage";
import SupervisorJobCardsPage from "@/pages/SupervisorJobCardsPage";
import SupervisorOrdersPage from "@/pages/SupervisorOrdersPage";
import SupervisorProductionQueuePage from "@/pages/SupervisorProductionQueuePage";
import SupervisorJobCardDetailPage from "@/pages/SupervisorJobCardDetailPage";
import SupervisorOrderDetailPage from "@/pages/SupervisorOrderDetailPage";

// The supervisor's app — the Android build's five screens, in the phone's shell
import PhoneShell from "@/components/phone/PhoneShell";
import SupervisorBoardPage from "@/pages/SupervisorApp/BoardPage";
import SupervisorPlatesPage from "@/pages/SupervisorApp/PlatesPage";
import SupervisorScanPage from "@/pages/SupervisorApp/ScanPage";
import SupervisorDispatchPage from "@/pages/SupervisorApp/DispatchPage";
import SupervisorProfilePage from "@/pages/SupervisorApp/ProfilePage";
import SupervisorPassedHistoryPage from "@/pages/SupervisorApp/PassedHistoryPage";
import SupervisorNotificationsPage from "@/pages/SupervisorApp/NotificationsPage";
import SupervisorJobCardPage from "@/pages/SupervisorApp/JobCardPage";
import SupervisorJobCardEditScreen from "@/pages/SupervisorApp/JobCardEditPage";
import RecycleBinPage from "@/pages/RecycleBinPage";
import ProductionStagesPage from "@/pages/ProductionStagesPage";
import AttachmentCategoriesPage from "@/pages/AttachmentCategoriesPage";
import BoxCostingPage from "@/pages/BoxCostingPage";
import ReportsPage from "@/pages/ReportsPage";

// Machine operator roles that can access My Production Queue
const MACHINE_OPERATOR_ROLES = ['machine', 'machine_man', 'supervisor', 'binder'];
const SUPERVISOR_ROLES = ['supervisor', 'admin'];

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangePassword(user)) return <Navigate to="/change-password" replace />;
  
  // Support multi-role: check if ANY of user's roles is in allowedRoles
  if (allowedRoles) {
    const userRoles = user.roles || [user.role];
    const hasPermission = userRoles.some(r => allowedRoles.includes(r));
    if (!hasPermission) {
      return <Navigate to="/" replace />;
    }
  }
  return children;
}

// Role-specific default landing pages
// Every role needs an entry. Without one the fallback is '/', which is the
// admin dashboard and immediately redirects to the landing page — itself — so
// the three shop-floor roles could not get past the login screen at all.
const ROLE_LANDING_PAGES = {
  admin: '/',
  receptionist: '/orders',
  // head_designer: '/design-queue',  // post open — restore with the role
  designer: '/my-designs',
  accountant: '/payments',
  store_keeper: '/inventory',
  dispatcher: '/dispatch',
  city_office_manager: '/orders',
  supervisor: '/supervisor',
  machine: '/my-production-queue',
  machine_man: '/my-production-queue',  // legacy accounts, never migrated
  binder: '/my-production-queue',
  // Drivers work from the mobile app and have no web screen of their own.
  // Orders is the one page any authenticated role can load — /dispatch would
  // only trade the redirect loop for a blank page, since four of its six
  // parallel requests are manager-only and it loads them with Promise.all.
  driver: '/orders',
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const landingPage = user ? (ROLE_LANDING_PAGES[user.role] || '/') : '/login';

  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={landingPage} replace />} />
      <Route path="/change-password" element={user ? <ChangePasswordPage /> : <Navigate to="/login" replace />} />
      <Route path="/" element={
        user?.role === 'admin' ? (
          <ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>
        ) : (
          <Navigate to={landingPage} replace />
        )
      } />
      <Route path="/orders" element={
        <ProtectedRoute><Layout><OrdersPage /></Layout></ProtectedRoute>
      } />
      <Route path="/orders/new" element={
        <ProtectedRoute><Layout><CreateOrderPage /></Layout></ProtectedRoute>
      } />
      <Route path="/orders/:id" element={
        <ProtectedRoute><Layout><OrderDetailPage /></Layout></ProtectedRoute>
      } />
      <Route path="/orders/:id/edit" element={
        <ProtectedRoute allowedRoles={ORDER_EDITOR_ROLES}>
          <Layout><EditOrderPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/job-card/:jobCardId" element={
        <ProtectedRoute><Layout><JobCardPage /></Layout></ProtectedRoute>
      } />
      <Route path="/job-card/:jobCardId/fill" element={
        <ProtectedRoute allowedRoles={["admin", "supervisor", "designer", "receptionist"]}>
          <Layout><JobCardFillPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute><Layout><CustomersPage /></Layout></ProtectedRoute>
      } />
      <Route path="/customers/:customerId" element={
        <ProtectedRoute><Layout><CustomerProfilePage /></Layout></ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={USER_MANAGER_ROLES}>
          <Layout><UsersPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/design-queue" element={
        <ProtectedRoute allowedRoles={["admin", "receptionist", "designer"]}>
          <Layout><DesignQueuePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-designs" element={
        <ProtectedRoute allowedRoles={["designer"]}>
          <Layout><MyDesignsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/production" element={
        <ProtectedRoute allowedRoles={["admin", "supervisor", "receptionist"]}>
          <Layout><ProductionPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-production-queue" element={
        <ProtectedRoute allowedRoles={["admin", "supervisor", "machine", "machine_man", "binder"]}>
          <Layout><MyProductionQueuePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute allowedRoles={["admin", "store_keeper", "receptionist"]}>
          <Layout><InventoryPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/dispatch" element={
        <ProtectedRoute allowedRoles={["admin", "dispatcher", "receptionist", "accountant", "city_office_manager"]}>
          <Layout><DispatchPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/costing" element={
        <ProtectedRoute allowedRoles={AGGREGATE_MONEY_ROLES}>
          <Layout><BoxCostingPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={["admin", "accountant", "city_office_manager", "dispatcher", "supervisor"]}>
          <Layout><ReportsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute allowedRoles={AGGREGATE_MONEY_ROLES}>
          <Layout><PaymentsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin-settings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminSettingsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/network-access" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><NetworkAccessPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/machines" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><MachinesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/catalog" element={
        <ProtectedRoute allowedRoles={["admin", "receptionist"]}>
          <Layout><ProductCatalogPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/catalog/new" element={
        <ProtectedRoute allowedRoles={["admin", "receptionist"]}>
          <Layout><CreateCatalogItemPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/catalog/:itemId" element={
        <ProtectedRoute allowedRoles={["admin", "receptionist"]}>
          <Layout><CatalogItemDetailPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/catalog/:itemId/edit" element={
        <ProtectedRoute allowedRoles={["admin", "receptionist"]}>
          <Layout><EditCatalogItemPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/job-cards" element={
        <ProtectedRoute allowedRoles={["admin", "supervisor", "designer", "receptionist"]}>
          <Layout><JobCardsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/external-processing" element={
        <ProtectedRoute allowedRoles={["admin", "dispatcher", "city_office_manager"]}>
          <Layout><ExternalProcessingPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><VendorManagementPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/plates" element={
        <ProtectedRoute allowedRoles={["admin", "designer", "supervisor", "receptionist"]}>
          <Layout><PlateTrackingPage /></Layout>
        </ProtectedRoute>
      } />
      {/*
        ── The supervisor's app ──
        Five screens in the phone's own shell, matching the Android build tab for
        tab, because the iOS half of the floor is trained on the same screens and
        gets them here instead. See components/phone/PhoneShell.
      */}
      <Route path="/supervisor" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell><SupervisorBoardPage /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/plates" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell><SupervisorPlatesPage /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/scan" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell><SupervisorScanPage /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/dispatch" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell><SupervisorDispatchPage /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/profile" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell><SupervisorProfilePage /></PhoneShell>
        </ProtectedRoute>
      } />
      {/* Stack screens: no tab bar, a back arrow instead — as on the phone */}
      <Route path="/supervisor/history" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell tabs={false}><SupervisorPassedHistoryPage /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/notifications" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell tabs={false}><SupervisorNotificationsPage /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/job-card/:id" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell tabs={false}><SupervisorJobCardPage /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/job-card/:id/edit" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell tabs={false}><SupervisorJobCardEditScreen /></PhoneShell>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/order/:id" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <PhoneShell tabs={false}><SupervisorOrderDetailPage /></PhoneShell>
        </ProtectedRoute>
      } />

      {/*
        ── Pre-app supervisor screens ──
        Off the supervisor's navigation: the phone replaced the dashboard, the
        four-tab job card list, the order list and the production queue with one
        board, and showing both would be two answers to the same question. They
        stay routed for an admin who wants the wide view, and because
        /supervisor/job-card/:id/advanced is the only place the pipeline
        overrides — skip, reroute, add process, remove stage — are reachable at
        all. The phone build has never had them.
      */}
      <Route path="/supervisor/dashboard" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <Layout><SupervisorDashboardPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/job-cards" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <Layout><SupervisorJobCardsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/orders" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <Layout><SupervisorOrdersPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/orders/new" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <Layout><CreateOrderPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/production-queue" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <Layout><SupervisorProductionQueuePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/job-card/:id/advanced" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <Layout><SupervisorJobCardDetailPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/supervisor/orders/:id" element={
        <ProtectedRoute allowedRoles={SUPERVISOR_ROLES}>
          <Layout><SupervisorOrderDetailPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/recycle-bin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><RecycleBinPage /></Layout>
        </ProtectedRoute>
      } />
      {/* Admin configuration routes */}
      <Route path="/admin-settings/stages" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><ProductionStagesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin-settings/attachment-categories" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AttachmentCategoriesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to={landingPage} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <MnemonicProvider>
            <AppRoutes />
            <MnemonicDevOverlay />
          </MnemonicProvider>
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
